import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
} from 'react'

/**
 * PollingControlContextType defines the shape of the context value.
 * - isPollingHalted: boolean indicating if polling is currently halted.
 * - haltPolling: function to halt polling.
 * - resumePolling: function to resume polling.
 */
interface PollingControlContextType {
	isPollingHalted: boolean
	haltPolling: () => void
	resumePolling: () => void
}

// Create a React Context for polling control, initially undefined.
// This context will be used to share polling state and controls across the app.
const PollingControlContext = createContext<
	PollingControlContextType | undefined
>(undefined)

/**
 * PollingControlProvider wraps part of the React tree that needs to control or observe polling.
 * It provides state and functions to halt/resume polling to all descendants via context.
 */
export function PollingControlProvider({
	children,
}: {
	children: React.ReactNode
}) {
	// State to track whether polling is halted.
	const [isPollingHalted, setIsPollingHalted] = useState(false)

	/**
	 * haltPolling sets isPollingHalted to true, effectively pausing any polling logic
	 * in consumers of this context. Wrapped in useCallback for referential stability.
	 */
	const haltPolling = useCallback(() => {
		console.log('[PollingControlContext] Halting polling.')
		setIsPollingHalted(true)
	}, [])

	/**
	 * resumePolling sets isPollingHalted to false, resuming polling logic
	 * in consumers of this context. Wrapped in useCallback for referential stability.
	 */
	const resumePolling = useCallback(() => {
		console.log('[PollingControlContext] Resuming polling.')
		setIsPollingHalted(false)
	}, [])

	// Memoize the context value to avoid unnecessary re-renders in consumers.
	const value = useMemo(
		() => ({ isPollingHalted, haltPolling, resumePolling }),
		[isPollingHalted, haltPolling, resumePolling],
	)

	// Provide the context value to all children.
	return (
		<PollingControlContext.Provider value={value}>
			{children}
		</PollingControlContext.Provider>
	)
}

/**
 * usePollingControl is a custom hook to access the polling control context.
 * It throws an error if used outside of a PollingControlProvider.
 * Returns:
 *   - isPollingHalted: boolean
 *   - haltPolling: function
 *   - resumePolling: function
 */
export function usePollingControl() {
	const context = useContext(PollingControlContext)
	if (context === undefined) {
		throw new Error(
			'usePollingControl must be used within a PollingControlProvider',
		)
	}
	return context
}

// ---
// How this works:
// - The PollingControlProvider component should wrap any part of your app that needs to control or observe polling state.
// - Components can call usePollingControl() to get access to isPollingHalted, haltPolling, and resumePolling.
// - This enables coordinated pausing/resuming of polling logic (e.g., for SSE, fetchers, etc.) across the app.
